/**
 * World 4 — "Storm Summit" (Thunder). Narrative fragment for campaignStories.ts.
 *
 * THE TALE (Act I, chapter 4): the climb ends in Vera's unmasking — fallen
 * Guardian-line of the Vault, deserter of the Final Seal. Old Man Gray reached
 * the Voltaryx node first: the saga's first GUTTERING trial. The storm-soul
 * ("Rumble") has forgotten its own thunder mid-battle, and losing here
 * flickers the flame lower and threads a strange voice closer. The trial is
 * won when Vera steps between the squads and names the thunder back to it —
 * a deserter teaching a Guardian how to hold its post. Saving kindness: Vera
 * standing her ground.
 */

import type { NodeStory } from '../../campaignStories.js';

export const WORLD4_TAGLINE =
  "Lightning has blocked the way to the top. The Guardian up there keeps hitting something in the clouds. Vera thinks she knows why.";

export const WORLD4_ZONE_STORIES: Record<string, string> = {
  z9: "Climb between the lightning strikes. Someone's muddy bootprints already lead all the way up.",
  z10: "The caves offer shelter, if you can hear yourself think. Vera stops whenever the thunder answers.",
  z11: "Above the last shelter, Rumble fights its own echo. You'll have to get close enough for Vera to call to it.",
};

export const WORLD4_NODE_STORIES: Record<string, NodeStory> = {
  // --- Zone 9: Thunder Steps (1v1) ---
  "z9-n1": {
    story: "Timmy waits on the wet steps, grinning. Chirpy just knocked a big Suikamon flat, and Timmy wants you next.",
    dialogue: {
      intro: "Told you, rival! Chirpy won the warm-up. Now Jolt wants a turn. Ready?",
      playerWin: "I lost! Jolt's sparks made my hair stand up, though!",
      playerLose: "A win! Chirpy, don't celebrate on the railing!",
      introRematchAfterLoss: "Another round? Stay under the rock. Chirpy's not a lightning rod.",
      introRematchAfterWin: "Hey! A sad old man went up before us! He waved at Chirpy. Then he forgot her name!",
      playerWinRepeat: "Lost again! Go be a legend up there.",
      playerLoseRepeat: "Another win! That's ten. Or three. Let's cheer quietly. The thunder's loud enough.",
    },
  },
  "z9-n2": {
    story: "The wind nearly knocks you over. Mimi is flying a kite in it anyway, laughing.",
    dialogue: {
      intro: "Everyone else turned back. Not us! Bet you turn back first!",
      playerWin: "No fair! You never quit!",
      playerLose: "Ha! Pay up! Downhill is that way!",
      introRematchAfterLoss: "Back for round two? I bet Lulu you'd sulk.",
      introRematchAfterWin: "The caves are next. Windy. Bring a coat!",
      playerWinRepeat: "Every bet, you win. One more, double or nothing!",
      playerLoseRepeat: "Wind wins! Try the next ledge!",
    },
  },
  "z9-n3": {
    story: "Lightning cracks around you. Lulu has worked out a route between the strikes.",
    dialogue: {
      intro: "I found a route between the strikes. It's good. My team is better. Want to check?",
      playerWin: "...You win. Stay close. We move after the next flash.",
      playerLose: "You're rushing. Lightning likes people who rush.",
      introRematchAfterLoss: "The path hasn't moved. Neither have I.",
      introRematchAfterWin: "Ready? We can fit a match in before the next squall.",
      playerWinRepeat: "...You win again. Go.",
      playerLoseRepeat: "The lightning is fast. So am I.",
    },
  },
  "z9-n4": {
    story: "At the last shelter, Cliff waves you in. Thistle, his Ray, clings on in the wind.",
    dialogue: {
      intro: "Last shelter before the caves. Thistle and I need to know you can manage the climb.",
      playerWin: "Sentry! We lost! You can manage. Keep low in the caves. The wind howls worse in there.",
      playerLose: "Stay here a bit. Your team needs a breather.",
      introRematchAfterLoss: "The landing holds. Sentry knew you'd come back.",
      introRematchAfterWin: "Sentry's been restless since the wind changed. Something up there forgot itself.",
      playerWinRepeat: "Beat again! Sentry watches the next ridge for you.",
      playerLoseRepeat: "The landing holds. My shelter, my rules.",
    },
  },

  // --- Zone 10: Howling Caves (2v2) ---
  "z10-n1": {
    story: "Thunder rolls over you, and Vera tilts her head. She hears two voices in it.",
    dialogue: {
      intro: "The thunder has two voices tonight. The spirits don't like it. Battle me before it gets louder.",
      playerWin: "The spirits went quiet... The Guardian up top is tired. Its thunder sounds like a cough.",
      playerLose: "Rest here. You won't hear much over your own panting.",
      introRematchAfterLoss: "Listen for the gap. There. That's when you move.",
      introRematchAfterWin: "The storm calls, and something answers. Quieter every year, like a friend walking away.",
      playerWinRepeat: "You heard it that time. Let's get out of these caves.",
      playerLoseRepeat: "Too early. Wait for the thunder to finish, then try again.",
    },
  },
  "z10-n2": {
    story: "Zip's voice echoes through the cave. Then Dash growls, and dust rains on your head.",
    dialogue: {
      intro: "Dash evolved again! His growl shakes rocks loose now. Watch. 'Clan Z!' See? Extra backup!",
      playerIntro: "Then I'll beat all of you. Echoes too!",
      playerWin: "Retreat! Retreat! ...That's the echo, not a second retreat.",
      playerLose: "Ha! Too slow! Clan Z runs the caves!",
      introRematchAfterLoss: "Back in my caves? Dash is warmed up!",
      introRematchAfterWin: "I've rehearsed my victory shout. Don't make me waste it!",
      playerWinRepeat: "Fine! Keep your echo-y cave!",
      playerLoseRepeat: "Slowpoke! Clan Z holds the dark!",
    },
  },
  "z10-nw1": {
    story: "Two wild shapes charge at you through the tunnels, fur crackling.",
    dialogue: {
      intro: "A wild Toro storms out! Another crackles behind it!",
      playerWin: "The wild pair bolts back into the caves.",
      playerLose: "The wild pair drives you off the trail.",
      introRematchAfterLoss: "The dark rustles. The wild pair returns.",
      introRematchAfterWin: "The pair knows your scent now. Sparks fly.",
      playerWinRepeat: "The wild pair scatters into the howling dark.",
      playerLoseRepeat: "The pair holds the trail. The caves stay wild.",
    },
  },
  "z10-n3": {
    story: "Aggie stands at the cave mouth, eyes shut, tapping a beat. She opens one eye at you.",
    dialogue: {
      intro: "I used to battle to the beat of this storm. Still can. Want a demonstration?",
      playerWin: "Well done, child! That little Bireon has fought the sky alone for years. Vera knows why.",
      playerLose: "Not quite. Let's try again here, out of the rain.",
      introRematchAfterLoss: "One more verse, child? My team knows them all.",
      introRematchAfterWin: "Every thunderclap is that Guardian's own voice, child. It just doesn't know it anymore.",
      playerWinRepeat: "Its own thunder bounces off the cliffs, and it attacks the sound. Get Vera up there, child.",
      playerLoseRepeat: "Another rest, another try. That worked for me at your age.",
    },
  },
  "z10-n4": {
    story: "By the fire, Vera grips the edge of her hood. She takes a deep breath and looks at you.",
    dialogue: {
      intro: "My family guarded the Storm Vault for nine generations. I ran from my post. I won't run from this storm.",
      playerWin: "Well fought. I'm coming to the summit with you. I've done enough running.",
      playerLose: "We'll try again. I'm staying, and so are you.",
      introRematchAfterLoss: "You're back. Good. Nobody has to make this climb alone.",
      introRematchAfterWin: "I ran away once, on the worst night of my life. Not this time.",
      playerWinRepeat: "You win. I'm still coming to the summit. You earned the company.",
      playerLoseRepeat: "Rest here a moment. We'll go up together.",
    },
  },

  // --- Zone 11: Storm Crown (3v3) ---
  "z11-n1": {
    story: "Wind howls at the summit. You can barely stand, but Shade flies above Zap.",
    dialogue: {
      intro: "Zip's echoes didn't stop you. Shade evolved again. Nothing else flies in this wind. Try him.",
      playerIntro: "Okay, Shade. Let's see you fly.",
      playerWin: "Up you go. Tell Zip I lasted longer.",
      playerLose: "Quiet wins. Clan Z keeps the crown.",
      introRematchAfterLoss: "Back on the trail. Shade watched you.",
      introRematchAfterWin: "Shade studied your squad all night.",
      playerWinRepeat: "You win. Even Shade can't stop you.",
      playerLoseRepeat: "The trail stays shut. Clan Z holds.",
    },
  },
  "z11-n2": {
    story: "Timmy is rehearsing a cheering song for the Guardian. He wants you to hear it.",
    dialogue: {
      intro: "I've written Rumble a cheering song! The thunder Bireon deserves one. Battle first, rehearsal after!",
      playerWin: "I lost! You lead. We'll follow with the chorus. It's mostly Chirpy.",
      playerLose: "We won! That gets a verse. Sorry, the song's getting longer.",
      introRematchAfterLoss: "Back already? Chirpy practiced her storm-face. Very fierce!",
      introRematchAfterWin: "Tell that lonely storm it has friends! We'll be right behind you, loudly!",
      playerWinRepeat: "We lost. I'm taking that verse about winning out of the song.",
      playerLoseRepeat: "Chirpy wins! I keep count. It's lots!",
    },
  },
  "z11-n3": {
    story: "Aggie waits for you at the foot of a long stair. She looks all the way up it, very still.",
    dialogue: {
      intro: "I turned back at this stair once. Let's see you through it properly.",
      playerWin: "Well done, child! Stay close to Vera. When she calls, the storm will listen.",
      playerLose: "Rest by me. The stair will still be here after tea.",
      introRematchAfterLoss: "You came back on your own. That's not nothing, child.",
      introRematchAfterWin: "Remember, child. A kind name is never wasted.",
      playerWinRepeat: "Well fought, child. Up you go. I'll watch the stair.",
      playerLoseRepeat: "Sit with me a moment. There's a dry spot behind this rock.",
    },
  },
  "z11-nw2": {
    story: "Hooves thunder. A sparking herd stampedes down the crown path at you.",
    dialogue: {
      intro: "A wild Sumiya hops out of the storm! A sparking herd charges right behind it!",
      playerWin: "The wild herd scatters up the crown path.",
      playerLose: "The wild herd chases you back down the path.",
      introRematchAfterLoss: "The storm churns. The wild herd returns.",
      introRematchAfterWin: "The herd knows your squad now. It stamps and sparks.",
      playerWinRepeat: "The wild herd bolts into the thunderheads.",
      playerLoseRepeat: "The herd holds the crown. The storm stays wild.",
    },
  },
  "z11-n4": {
    story: "Rumble, the thunder Bireon, strikes at each thunderclap. A bolt splits the ledge under Vera, right beside you.",
    dialogue: {
      intro: "Rumble thinks its own echo is an enemy. Hold on while Vera calls to it.",
      playerWin: "Vera calls Rumble's name into the storm. Rumble hears its own song in her voice and stops fighting the echo. The sky clears, and its shard lands in your Shard Book. Nine!",
      playerLose: "Rumble's thunder drives you back down the Crown.",
      introRematchAfterLoss: "Rumble's shard flickers lower. Hurry. Another voice is whispering in the wind.",
      introRematchAfterWin: "You came back to visit. Rumble booms a glad hello.",
      playerWinRepeat: "Rumble answers Vera at once. Its shard shines steadily again.",
      playerLoseRepeat: "Rumble wins this round and booms happily. Vera just laughs. 'Again!'",
    },
  },
};
